import { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { UserPreferences } from '../types';

export function useUserPreferences(userId: string | null) {
  const [preferences, setPreferences] = useState<UserPreferences>({
    disabledShortcuts: [],
    customExpansions: {}
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setPreferences({ disabledShortcuts: [], customExpansions: {} });
      setLoading(false);
      return;
    }

    const docRef = doc(db, 'userPreferences', userId);
    
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setPreferences(snapshot.data() as UserPreferences);
        } else {
          setPreferences({ disabledShortcuts: [], customExpansions: {} });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching preferences:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const toggleShortcut = async (shortcutId: string) => {
    if (!userId) return;
    
    const newDisabled = preferences.disabledShortcuts.includes(shortcutId)
      ? preferences.disabledShortcuts.filter(id => id !== shortcutId)
      : [...preferences.disabledShortcuts, shortcutId];
    
    try {
      await setDoc(
        doc(db, 'userPreferences', userId),
        { ...preferences, disabledShortcuts: newDisabled },
        { merge: true }
      );
    } catch (error) {
      console.error('Error toggling shortcut:', error);
      throw error;
    }
  };

  const updateCustomExpansion = async (shortcutId: string, expansion: string) => {
    if (!userId) return;
    
    const newCustomExpansions = { ...preferences.customExpansions };
    
    if (expansion) {
      newCustomExpansions[shortcutId] = expansion;
    } else {
      delete newCustomExpansions[shortcutId];
    }
    
    try {
      await setDoc(
        doc(db, 'userPreferences', userId),
        { ...preferences, customExpansions: newCustomExpansions },
        { merge: true }
      );
    } catch (error) {
      console.error('Error updating custom expansion:', error);
      throw error;
    }
  };

  return { preferences, loading, toggleShortcut, updateCustomExpansion };
}